export const PIPELINE_STAGES = [
  "claimed",
  "invited",
  "signed_up",
  "self_accredited",
  "call_scheduled",
  "call_completed",
  "deal_room_access",
  "committed",
  "closed",
] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export const INVESTOR_PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  claimed: "Claimed",
  invited: "Invited",
  signed_up: "Signed Up",
  self_accredited: "Self-Accredited",
  call_scheduled: "Call Scheduled",
  call_completed: "Call Completed",
  deal_room_access: "Deal Access Granted",
  committed: "Committed",
  closed: "Closed",
};

/**
 * Active investors eligible for a project grant, regardless of consultation
 * status. Applies to both ladders in `shared/investor-ladders.ts`:
 * - referral: partner may grant ahead; materials unlock after self-certify
 * - organic: ops grants after eligibility (+ interest); call is optional
 */
export const DEAL_ROOM_ELIGIBLE_STAGES: readonly PipelineStage[] = [
  "self_accredited",
  "call_scheduled",
  "call_completed",
  "deal_room_access",
  "committed",
];

export function isDealRoomEligibleStage(stage: string): boolean {
  return (DEAL_ROOM_ELIGIBLE_STAGES as readonly string[]).includes(stage);
}

/**
 * Invite send is a correction for never-activated accounts, not a forward
 * auto-advance: `signed_up` sits *after* `invited` in the canonical order
 * (organic signups skip invited), but admin/partner-added leads are often
 * still labeled `signed_up` before they ever log in. Stamping `invited`
 * on a successful activation-invite is allowed from claimed or signed_up.
 */
export function shouldStampInvitedOnInvite(
  currentStage: string,
  isOverride: boolean,
): boolean {
  if (isOverride) return false;
  return currentStage === "claimed" || currentStage === "signed_up";
}

// Task #2072 — deal-room / commit access gate. Verification documents are
// no longer collected; passing the accreditation test (self-certify) is now
// the meaningful gate. Legacy investors with a manually "verified" status are
// still accepted.
export function isAccreditedForDealRoom(
  accreditationStatus: string | null | undefined,
): boolean {
  return accreditationStatus === "self_certified" || accreditationStatus === "verified";
}

export function shouldAutoAdvance(
  currentStage: string,
  targetStage: string,
  isOverride: boolean,
): boolean {
  if (isOverride) return false;

  const currentIdx = PIPELINE_STAGES.indexOf(currentStage as PipelineStage);
  const targetIdx = PIPELINE_STAGES.indexOf(targetStage as PipelineStage);

  if (currentIdx === -1 || targetIdx === -1) {
    return false;
  }

  return targetIdx > currentIdx;
}
